// src/api/orders.ts
import { apiClient } from './client';
import type { Order, OrderItem, ApiResponse, PaginatedResponse } from '../types';
import type OrderDetail from '../pages/Admin/Orders/OrderDetail';

export const ordersAPI = {
  // Get all orders (with filters)
  async getOrders(params?: {
    status?: string;
    page?: number;
    search?: string;
  }): Promise<ApiResponse<Order[]>> {
    try {
      const response = await apiClient.get<Order[]>('/orders/orders/', { params });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch orders',
        status: error.response?.status || 500,
      };
    }
  },

  // Update order status
  async updateOrderStatus(
    orderId: number,
    status: string,
    notes?: string
  ): Promise<ApiResponse<Order>> {
    try {
      // Remove duplicate /orders/ from URL
      const response = await apiClient.patch(
        `/orders/${orderId}/`,
        { status, notes }
      );
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to update order status',
        status: error.response?.status || 500,
      };
    }
  },


  // Assign driver to order
  async assignDriver(orderId: number, driverId: number): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/orders/${orderId}/assign-driver/`, { driver_id: driverId });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to assign driver',
        status: error.response?.status || 500
      };
    }
  },

  async createOrder(orderData: any): Promise<ApiResponse<Order>> {
    try {
      const response = await apiClient.post<Order>('/orders/create/', orderData);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to create order',
        status: error.response?.status || 500,
      };
    }
  },

  // Get user's orders
  async getMyOrders(): Promise<ApiResponse<Order[]>> {
    try {
      const response = await apiClient.get<Order[]>('/orders/my_orders/');
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch orders',
        status: error.response?.status || 500,
      };
    }
  },

  // Get single order
  async getOrder(orderNumber: string): Promise<ApiResponse<Order>> {
    try {
      const response = await apiClient.get<Order>(`/orders/${orderNumber}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch order',
        status: error.response?.status || 500,
      };
    }
  },

  // Cancel order
  async cancelOrder(orderNumber: number, reason: string, reasonCode?: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post(`/orders/${orderNumber}/cancel/`, {
        reason,
        reason_code: reasonCode,
      });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to cancel order',
        status: error.response?.status || 500,
      };
    }
  },

  // Track order
  async trackOrder(trackingNumber: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get<any>(`/orders/track/${trackingNumber}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to track order',
        status: error.response?.status || 500,
      };
    }
  },

  // Download invoice
  async downloadInvoice(orderNumber: string): Promise<Blob> {
    const response = await apiClient.get(`/orders/${orderNumber}/invoice/`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async getDeliveryOptions(data: {
    address_id: number;
    items: Array<{
      product_variant_id: number;
      quantity: number;
      price?: number;
    }>;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post('/orders/delivery-options/', data);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.error || 'Failed to calculate delivery options',
        status: error.response?.status || 500
      };
    }
  },

  async getAdminOrders(params: {
    page?: number;
    page_size?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<PaginatedResponse<Order>>> {
    try {
      const response = await apiClient.get('/orders/admin/list/', { params });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch orders',
        status: error.response?.status || 500
      };
    }
  },

  async getAdminOrder(orderNumber: string): Promise<ApiResponse<OrderDetail>> {
    try {
      const response = await apiClient.get(`/orders/${orderNumber}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch order',
        status: error.response?.status || 500
      };
    }
  }

};

export default ordersAPI;